// apiClient.js - Utility for making authenticated API calls

/**
 * Retrieves the authentication token from localStorage.
 * @returns {string|null} The token or null if not found or error occurs.
 */
function Lux_ApiClient_getAuthToken() {
  const authString = localStorage.getItem('auth')
  if (!authString) {
    console.error('Auth data not found in localStorage.')
    return null
  }
  try {
    const authData = JSON.parse(authString)
    if (!authData?.token) {
      console.error('Token not found within auth data in localStorage.')
      return null
    }
    return authData.token
  } catch (e) {
    console.error('Error parsing auth data from localStorage:', e)
    return null
  }
}

/**
 * Makes an authenticated fetch request to a relative API endpoint.
 * @param {string} relativeUrl - The relative URL of the API endpoint (e.g., '/api/users').
 * @param {object} options - Fetch options (method, headers, body, etc.). Body should be stringified JSON if applicable.
 * @returns {Promise<Response>} The fetch Response object.
 * @throws {Error} If the auth token cannot be retrieved.
 */
async function Lux_ApiClient_fetch(relativeUrl, options = {}) {
  const token = Lux_ApiClient_getAuthToken()
  if (!token) {
    throw new Error('Authentication token not available. Cannot make API call.')
  }

  const defaultHeaders = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json', // Default content type
  }

  const mergedOptions = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...(options.headers || {}), // Merge custom headers, allowing override
    },
  }

  // Ensure URL is relative (or handle absolute if needed, but relative is preferred)
  const finalUrl = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`;

  console.log(
    `ApiClient: Making ${mergedOptions.method || 'GET'} request to ${finalUrl}`
  )

  return fetch(finalUrl, mergedOptions)
}

/**
 * Helper for making a POST request with JSON body.
 * @param {string} relativeUrl - The relative URL.
 * @param {object} data - The data object to be sent as JSON.
 * @param {object} fetchOptions - Additional fetch options.
 * @returns {Promise<Response>} The fetch Response object.
 */
async function Lux_ApiClient_post(relativeUrl, data, fetchOptions = {}) {
  return Lux_ApiClient_fetch(relativeUrl, {
    method: 'POST',
    body: JSON.stringify(data),
    ...fetchOptions,
  })
}

/**
 * Helper for making a GET request.
 * @param {string} relativeUrl - The relative URL.
 * @param {object} fetchOptions - Additional fetch options.
 * @returns {Promise<Response>} The fetch Response object.
 */
async function Lux_ApiClient_get(relativeUrl, fetchOptions = {}) {
  return Lux_ApiClient_fetch(relativeUrl, {
    method: 'GET',
    ...fetchOptions,
  })
}

/**
 * Saves comment and/or status for an account balance, handling mode.
 * @param {object} data - The data object.
 * @param {number} data.postingItemId - The ID of the balance item.
 * @param {string|null} data.comment - The comment text.
 * @param {string} [data.status] - Optional status to set.
 * @param {'single order'|'standing order'} data.mode - The mode of the update.
 * @param {boolean} [data.confirmedOverwrite=false] - Whether overwrite warning was confirmed.
 * @param {string} [data.startDate] - Optional start date for standing order mode.
 * @param {string} [data.endDate] - Optional end date for standing order mode.
 * @param {object} [fetchOptions={}] - Additional fetch options.
 * @returns {Promise<Response>} The raw fetch Response object (caller needs to check status).
 */
async function Lux_ApiClient_saveAccountBalanceComment(
  data,
  fetchOptions = {}
) {
  // Basic validation
  if (typeof data?.postingItemId !== 'number') {
    throw new Error(
      'postingItemId is required in data for saveAccountBalanceComment.'
    )
  }
  if (!['single order', 'standing order'].includes(data.mode)) {
    throw new Error(
      "mode must be 'single order' or 'standing order' in data for saveAccountBalanceComment."
    )
  }

  const payload = {
    postingItemId: data.postingItemId,
    comment: data.comment, // Can be null
    mode: data.mode,
    confirmedOverwrite: data.confirmedOverwrite || false, // Default to false
    userName: data.userName,
  }
  // Only include status if it's provided
  if (data.status) {
    payload.status = data.status
  }

  return Lux_ApiClient_post('/api/account-balances', payload, fetchOptions)
}

/**
 * Approves all balances for a given exchange plan within a date range.
 * @param {object} data - The data object.
 * @param {number} data.exchangePlanId - The ID of the exchange plan (invoice).
 * @param {string} data.startDate - Start date (YYYY-MM-DD).
 * @param {string} data.endDate - End date (YYYY-MM-DD).
 * @param {object} [fetchOptions={}] - Additional fetch options.
 * @returns {Promise<Response>} The fetch Response object.
 */
async function Lux_ApiClient_approveAllBalances(data, fetchOptions = {}) {
  // Basic validation
  if (typeof data?.exchangePlanId !== 'number') {
    throw new Error('exchangePlanId is required for approveAllBalances.')
  }
  if (
    !data.startDate ||
    !data.endDate ||
    typeof data.startDate !== 'string' ||
    typeof data.endDate !== 'string'
  ) {
    throw new Error(
      'startDate and endDate are required strings for approveAllBalances.'
    )
  }

  const payload = {
    action: 'approveAll',
    exchangePlanId: data.exchangePlanId,
    startDate: data.startDate,
    endDate: data.endDate,
  }

  return Lux_ApiClient_post('/api/account-balances', payload, fetchOptions)
}
